import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import Seo from "./Seo";
import { PRESS_CONTACT_EMAIL, FEATURED_PRESS } from "./constants";
import { Mail, Download, FileText, User, ExternalLink, Megaphone } from "lucide-react";

export default function Press() {
  return (
    <MarketingShell>
      <Seo title="Press & Media" description="Sister Signal — a women-led, lived-experience-built emotional safety companion. Press contact, media kit, and coverage." path="/press" />
      <article className="max-w-3xl mx-auto px-6 sm:px-10 py-20" data-testid="page-press">
        <p className="font-script text-2xl text-brand">for journalists & media</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">Press & Media.</h1>
        <p className="mt-5 text-lg text-foreground/75 leading-relaxed">
          Sister Signal is a women-led, lived-experience-built emotional safety companion. If you're
          writing about domestic abuse, technology-for-good, or survivor-built tools, we'd be honoured
          to help.
        </p>

        {/* FEATURED COVERAGE — modern press cards from FEATURED_PRESS ----- */}
        <section className="mt-12" data-testid="press-coverage">
          <p className="font-script text-xl text-brand">featured coverage</p>
          <h2 className="font-serif text-3xl mt-1 leading-tight">Recent stories.</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-5">
            {FEATURED_PRESS.map((c, i) => (
              <a
                key={i}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`press-coverage-link-${i}`}
                className="group rounded-3xl bg-card border border-border p-6 hover:border-brand/50 transition-all flex flex-col"
              >
                <p className="text-xs uppercase tracking-widest text-brand">{c.outlet} · {c.kind}</p>
                <h3 className="mt-2 font-serif text-2xl leading-snug text-foreground group-hover:text-brand transition-colors">
                  {c.headline}
                </h3>
                <p className="mt-3 text-sm text-foreground/70 leading-relaxed flex-1">{c.description}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand">
                  Read the story <ExternalLink className="w-3.5 h-3.5" />
                </span>
              </a>
            ))}
          </div>
        </section>

        <section className="mt-12 grid sm:grid-cols-2 gap-5">
          <a href={`mailto:${PRESS_CONTACT_EMAIL}`}
             data-testid="press-contact"
             className="rounded-2xl bg-card border border-border p-6 hover:border-brand/50 transition-all">
            <Mail className="w-5 h-5 text-brand" />
            <p className="mt-3 font-serif text-xl">Press contact</p>
            <p className="mt-1 text-sm text-foreground/70">{PRESS_CONTACT_EMAIL}</p>
            <p className="mt-2 text-xs text-foreground/55">We aim to respond within two business days.</p>
          </a>

          <Link to="/press/media-kit" data-testid="press-media-kit-link"
             className="rounded-2xl bg-card border border-border p-6 hover:border-brand/50 transition-all">
            <Download className="w-5 h-5 text-brand" />
            <p className="mt-3 font-serif text-xl">Media kit</p>
            <p className="mt-1 text-sm text-foreground/70">Logos, brand colors, taglines, boilerplate, founder bio, screenshots.</p>
            <p className="mt-2 text-xs text-brand">Open the media kit →</p>
          </Link>

          <a
            href="https://drive.google.com/file/d/1U8iMmm1u27lMzHOO95cWihCnzbDQbjXz/view?usp=share_link"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="press-advocate-kit-link"
            className="rounded-2xl bg-card border border-border p-6 hover:border-brand/50 transition-all"
          >
            <Megaphone className="w-5 h-5 text-brand" />
            <p className="mt-3 font-serif text-xl">Advocate &amp; Influencer Kit</p>
            <p className="mt-1 text-sm text-foreground/70">
              Everything you need to share Sister Signal with your community — ready-to-use captions,
              hashtags, app feature descriptions, and real quotes from survivors and advocates.
            </p>
            <p className="mt-2 text-xs text-brand">Download the Advocate Kit →</p>
          </a>

          <div className="rounded-2xl bg-card border border-border p-6">
            <User className="w-5 h-5 text-brand" />
            <p className="mt-3 font-serif text-xl">Founder bio</p>
            <p className="mt-1 text-sm text-foreground/70">
              Sister Signal was founded by a survivor of intimate partner abuse who could not find a tool
              that felt safe enough to use. Full bio available in the media kit and on request.
            </p>
          </div>

          <div className="rounded-2xl bg-card border border-border p-6">
            <FileText className="w-5 h-5 text-brand" />
            <p className="mt-3 font-serif text-xl">Quotes & data</p>
            <p className="mt-1 text-sm text-foreground/70">
              We are glad to provide founder quotes, survivor introductions (with consent), and product
              data for thoughtful pieces. Email us at <a className="text-brand hover:underline" href={`mailto:${PRESS_CONTACT_EMAIL}`}>{PRESS_CONTACT_EMAIL}</a>.
            </p>
          </div>
        </section>

        <section className="mt-12 rounded-3xl bg-brand-soft/40 border border-brand/20 p-8">
          <p className="font-script text-xl text-brand">a request</p>
          <p className="mt-1 font-serif text-2xl leading-snug">Please cover this work with care.</p>
          <p className="mt-3 text-foreground/75 leading-relaxed">
            Sister Signal serves a sensitive audience. We ask that any coverage avoid sensationalising abuse,
            link to support resources, and centre survivor voices.
          </p>
        </section>

        <Link to="/" className="mt-8 inline-block text-sm text-brand hover:underline">← Back to home</Link>
      </article>
    </MarketingShell>
  );
}
